import type React from "react";
import { useState } from "react";
import { useSidePanel } from "@/components/patterns";
import { CandidatoPanel, type CandidatoPanelTab } from "@/components/freelancers/painel/CandidatoPanel";
import { LeituraEmRevisao } from "@/components/freelancers/painel/CandidatoLinkedInTab";
import type { LinkedInJob } from "@/types/linkedin";
import { SlotDaChecklistProvider } from "./ChecklistDaRevisao";

const PAINEL = "candidato";

/**
 * O painel do candidato na aba LinkedIn com a leitura do job. Aberto, segue o job:
 * trocar de job troca o freelancer junto com a leitura.
 */
export function PainelDaLeitura({ job }: { job: LinkedInJob | null | undefined }) {
  const candidatoPanel = useSidePanel<CandidatoPanelTab>(PAINEL);
  const freelancerId = job?.extracted_json ? job.freelancer_id : null;
  return (
    <CandidatoPanel
      freelancerId={candidatoPanel.itemId ? freelancerId : null}
      tab={candidatoPanel.tab}
      onTabChange={candidatoPanel.setTab}
      onClose={candidatoPanel.close}
      linkedinJobId={job?.id}
    />
  );
}

interface LinkedInRevisaoComPerfilProps {
  job: LinkedInJob;
  /** A página monta o PainelDaLeitura fora da troca de jobs (lote), para ele não fechar a cada job. */
  painelNaPagina?: boolean;
  children: React.ReactNode;
}

/**
 * A revisão ao lado do perfil como no LinkedIn. Em telas largas o perfil fica
 * numa coluna fixa à direita, com a checklist da revisão no topo; nas estreitas,
 * o formulário abre o painel do candidato na aba LinkedIn com esta leitura.
 */
export function LinkedInRevisaoComPerfil({ job, painelNaPagina, children }: LinkedInRevisaoComPerfilProps) {
  const [slotDaChecklist, setSlotDaChecklist] = useState<HTMLDivElement | null>(null);
  const freelancerId = job.freelancer_id;
  if (!freelancerId || !job.extracted_json) return <>{children}</>;

  return (
    <SlotDaChecklistProvider value={slotDaChecklist}>
      <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start xl:gap-6">
        <div className="min-w-0">{children}</div>
        <aside className="sticky top-4 hidden max-h-[calc(100vh-6rem)] overflow-y-auto xl:block" data-testid="linkedin-revisao-perfil">
          <div ref={setSlotDaChecklist} className="sticky top-0 z-10 bg-background" />
          <LeituraEmRevisao freelancerId={freelancerId} jobId={job.id} linkedinUrl={null} />
        </aside>
      </div>
      {!painelNaPagina && <PainelDaLeitura job={job} />}
    </SlotDaChecklistProvider>
  );
}

/** Abre o painel do candidato na aba LinkedIn: o caminho das telas estreitas, onde a coluna não aparece. */
export function useAbrirPainelDaLeitura() {
  const candidatoPanel = useSidePanel<CandidatoPanelTab>(PAINEL);
  return (freelancerId: string) => candidatoPanel.open(freelancerId, "linkedin");
}
